import { useEffect, useState } from "react";
import { useProject } from "./ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ListTodo, Layers, Sparkles, Download, ChevronDown, GitBranch, CheckCircle2, ClipboardList, AlertTriangle, Wrench, Archive, FileSpreadsheet } from "lucide-react";

const PRIO_TONE = { High: "bg-red-50 text-red-600 ring-red-200", Medium: "bg-amber-50 text-amber-600 ring-amber-200", Low: "bg-slate-100 text-slate-500 ring-slate-200" };
const PHASE_TONE = { MVP: "bg-emerald-50 text-emerald-600 ring-emerald-200", "Phase 2": "bg-sky-50 text-sky-600 ring-sky-200", Future: "bg-violet-50 text-violet-600 ring-violet-200" };

export default function Backlog() {
  const { project } = useProject();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "consultant";
  const [bl, setBl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);

  const reload = () => api.get(`/projects/${project.id}/backlog`).then((r) => setBl(r.data));
  useEffect(() => { api.get(`/projects/${project.id}/backlog`).then((r) => setBl(r.data)).catch(() => toast.error("Could not load backlog")).finally(() => setLoading(false)); }, [project.id]);

  const generate = async () => {
    setGen(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/backlog/generate`);
      setBl(data);
      toast.success("Implementation backlog generated");
    } catch (e) { toast.error(apiErr(e, "Generation failed")); }
    finally { setGen(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/projects/${project.id}/backlog/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "implementation-backlog.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e, "Export failed")); }
  };

  const downloadXlsx = async () => {
    try {
      const res = await api.get(`/projects/${project.id}/backlog/export.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "implementation-backlog.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e, "Export failed")); }
  };

  const patchStory = (epicId, storyId, patch) => {
    setBl((prev) => ({ ...prev, epics: prev.epics.map((e) => e.id === epicId ? { ...e, stories: e.stories.map((s) => s.id === storyId ? { ...s, ...patch } : s) } : e) }));
    api.patch(`/projects/${project.id}/backlog/stories/${storyId}`, patch).catch(() => { toast.error("Could not save change"); reload(); });
  };

  const totalStories = bl?.epics?.reduce((n, e) => n + (e.stories?.length || 0), 0) || 0;

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-sky-600" />
              <h2 className="font-heading text-xl font-bold text-slate-900">Implementation Backlog</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">Validated discovery converted into Epics, User Stories & Acceptance Criteria — ready to hand to the delivery team.</p>
          </div>
          <div className="flex gap-2">
            {bl && <Button variant="outline" data-testid="export-backlog-csv" onClick={download} className="rounded-lg"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>}
            {bl && <Button variant="outline" data-testid="export-backlog-xlsx" onClick={downloadXlsx} className="rounded-lg"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Excel</Button>}
            {canManage && <Button data-testid="generate-backlog" onClick={generate} disabled={gen} className="rounded-lg bg-sky-600 hover:bg-sky-700"><Sparkles className="mr-1.5 h-4 w-4" /> {gen ? "Generating…" : bl ? "Regenerate" : "Generate Backlog"}</Button>}
          </div>
        </div>

        {loading && <div className="mt-10 text-center text-slate-400">Loading…</div>}

        {!loading && !bl && (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50"><ListTodo className="h-7 w-7 text-sky-500" /></div>
            <p className="font-heading text-lg font-bold text-slate-700">No backlog yet</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">Once discovery is filled in, generate a build-ready backlog of Epics, User Stories and Acceptance Criteria — split into in-scope work and future items.</p>
            {canManage && <Button onClick={generate} disabled={gen} className="mt-5 rounded-lg bg-sky-600 hover:bg-sky-700"><Sparkles className="mr-1.5 h-4 w-4" /> {gen ? "Generating…" : "Generate Backlog"}</Button>}
          </div>
        )}

        {bl && (
          <>
            <div className="mt-5 flex flex-wrap gap-3 text-sm">
              <Stat label="Epics" value={bl.epics?.length || 0} />
              <Stat label="User Stories" value={totalStories} />
              <Stat label="Future items" value={bl.future?.length || 0} />
            </div>

            <div className="mt-6 space-y-4">
              {bl.epics?.map((ep) => <EpicCard key={ep.id} epic={ep} canManage={canManage} onPatch={(sid, patch) => patchStory(ep.id, sid, patch)} />)}
            </div>

            {bl.future?.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-violet-500" /><h3 className="font-heading text-base font-bold text-slate-900">Future / Change-Request Items</h3></div>
                <p className="mt-1 text-sm text-slate-500">Requirements captured but beyond the agreed scope — tracked here, not in the build backlog.</p>
                <div className="mt-3 space-y-2">
                  {bl.future.map((f) => (
                    <div key={f.id} data-testid={`future-item-${f.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-violet-200 bg-violet-50/50 p-3">
                      <div><div className="text-sm font-semibold text-slate-800">{f.title}</div>{f.module && <div className="text-xs text-slate-500">{f.module}</div>}</div>
                      <span className="rounded-md bg-violet-100 px-2 py-1 text-xs font-bold text-violet-700">{f.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-6 text-center text-xs text-slate-400">Generated {bl.generated_at ? new Date(bl.generated_at).toLocaleString() : ""} {bl.generated_by ? `· by ${bl.generated_by}` : ""}</p>
          </>
        )}
        {bl?.captured?.length > 0 && <CapturedDetail captured={bl.captured} />}
      </div>
    </div>
  );
}

function CapturedDetail({ captured }) {
  const [open, setOpen] = useState(true);
  const total = captured.reduce((n, s) => n + (s.items?.length || 0), 0);
  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="backlog-captured-detail">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 p-5 text-left hover:bg-slate-50">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100"><Archive className="h-4.5 w-4.5 text-slate-600" /></div>
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">Captured Discovery Detail</h3>
            <p className="text-xs text-slate-500">Complete source record — every one of the {total} captured answers behind this backlog.</p>
          </div>
        </div>
        <ChevronDown className={"h-4 w-4 text-slate-400 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-slate-100 p-5">
          {captured.map((sec, i) => (
            <div key={i} data-testid={`backlog-captured-module-${i}`}>
              <div className="flex items-center gap-2">
                <div className="font-heading text-sm font-bold text-sky-700">{sec.module}</div>
                {sec.out_of_scope && <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-600 ring-1 ring-amber-200">Out of scope</span>}
              </div>
              <div className="mt-2 space-y-2">
                {sec.items?.map((it, j) => (
                  <div key={j} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                    <div className="text-xs font-semibold text-slate-500">{it.question}</div>
                    <div className="mt-0.5 text-sm text-slate-800">{it.answer}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="rounded-xl border border-slate-200 bg-white px-4 py-2"><span className="font-heading text-lg font-extrabold text-sky-600">{value}</span> <span className="text-xs font-semibold text-slate-500">{label}</span></div>;
}

function EpicCard({ epic, canManage, onPatch }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`epic-${epic.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 p-5 text-left hover:bg-slate-50">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50"><Layers className="h-4.5 w-4.5 text-violet-600" /></span>
          <div>
            <div className="font-heading text-base font-bold text-slate-900">{epic.title}</div>
            <div className="text-xs text-slate-500">{epic.module} · {epic.stories?.length || 0} stories</div>
          </div>
        </div>
        <ChevronDown className={"h-4 w-4 text-slate-400 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="border-t border-slate-100 p-5">
          {epic.description && <p className="mb-4 text-sm text-slate-500">{epic.description}</p>}
          <div className="space-y-3">
            {epic.stories?.map((s) => <StoryRow key={s.id} story={s} canManage={canManage} onPatch={(patch) => onPatch(s.id, patch)} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function StoryRow({ story, canManage, onPatch }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`story-${story.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
          <div>
            <div className="font-heading text-sm font-bold text-slate-800">{story.title}</div>
            <div className="mt-0.5 text-sm text-slate-600">{story.user_story}</div>
          </div>
        </div>
        {canManage ? (
          <div className="flex shrink-0 gap-1.5">
            <Select value={story.priority} onValueChange={(v) => onPatch({ priority: v })}>
              <SelectTrigger data-testid={`story-priority-${story.id}`} className="h-8 w-[92px] rounded-lg text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{["High", "Medium", "Low"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={story.phase} onValueChange={(v) => onPatch({ phase: v })}>
              <SelectTrigger data-testid={`story-phase-${story.id}`} className="h-8 w-[100px] rounded-lg text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{["MVP", "Phase 2", "Future"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div className="flex shrink-0 gap-1.5">
            <span className={"rounded-md px-2 py-1 text-xs font-bold ring-1 " + (PRIO_TONE[story.priority] || "")}>{story.priority}</span>
            <span className={"rounded-md px-2 py-1 text-xs font-bold ring-1 " + (PHASE_TONE[story.phase] || "")}>{story.phase}</span>
          </div>
        )}
      </div>
      {story.acceptance_criteria?.length > 0 && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400"><ClipboardList className="h-3.5 w-3.5" /> Acceptance Criteria</div>
          <ul className="space-y-1">
            {story.acceptance_criteria.map((a, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-600"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" /> {a}</li>
            ))}
          </ul>
        </div>
      )}
      {(story.salesforce_component || story.config_or_custom || story.estimate) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs" data-testid={`story-tech-${story.id}`}>
          {story.salesforce_component && <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 font-semibold text-indigo-600 ring-1 ring-indigo-200"><Wrench className="h-3 w-3" /> {story.salesforce_component}</span>}
          {story.config_or_custom && <span className="rounded-md bg-slate-100 px-2 py-1 font-semibold text-slate-600 ring-1 ring-slate-200">{story.config_or_custom}</span>}
          {story.estimate && <span className="rounded-md bg-teal-50 px-2 py-1 font-bold text-teal-600 ring-1 ring-teal-200">Est. {story.estimate}</span>}
        </div>
      )}
    </div>
  );
}
